import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { BookOpen, Users, Heart, Star, CheckCircle, Music, TreePine, Baby, Clock, PoundSterling, MessageCircle, Activity, Calculator, Globe, Palette } from "lucide-react";
import { childcareData } from "../data/mock";

const Services = () => {
  const activityIcons = {
    "Heart": Heart,
    "Users": Users,
    "BookOpen": BookOpen,
    "Star": Star,
    "Music": Music,
    "TreePine": TreePine
  };

  const eyfsIcons = {
    "MessageCircle": MessageCircle,
    "Activity": Activity,
    "Heart": Heart,
    "BookOpen": BookOpen,
    "Calculator": Calculator,
    "Globe": Globe,
    "Palette": Palette
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        
        {/* Hero Section */}
        <section className="py-20 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-slate-800 mb-6">
                Our Services & 
                <span style={{color: '#A7C7E7'}}> Curriculum</span>
              </h1>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Discover our comprehensive Early Years Foundation Stage curriculum and unique additional activities that make Heathbourne House truly special.
              </p>
            </div>

            {/* Key Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center mb-12">
              <div className="bg-white rounded-2xl p-6 border border-blue-200 shadow-sm">
                <Baby className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-blue-700 mb-2">{childcareData.services.ageRange}</h3>
                <p className="text-slate-600">Age Range</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-pink-200 shadow-sm">
                <Clock className="h-12 w-12 text-pink-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-pink-700 mb-2">{childcareData.services.hours}</h3>
                <p className="text-slate-600">Operating Hours</p>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-green-200 shadow-sm">
                <PoundSterling className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-green-700 mb-2">{childcareData.services.price}</h3>
                <p className="text-slate-600">All-Inclusive Rate</p>
              </div>
            </div>
          </div>
        </section>

        {/* EYFS Core Curriculum */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Early Years Foundation Stage 
                <span className="text-blue-500"> Curriculum</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-4xl mx-auto">
                {childcareData.services.eyfsCore.description}
              </p>
            </div>

            {/* Age Groups */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
              {childcareData.services.eyfsCore.ageGroups.map((group, index) => (
                <div key={index} className="bg-blue-50 rounded-3xl p-8 border border-blue-100 hover:shadow-lg transition-shadow">
                  <div className="text-center mb-6">
                    <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-lg">{group.age}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-slate-800 mb-2">{group.title}</h3>
                    <div className="w-16 h-1 bg-blue-400 rounded-full mx-auto"></div>
                  </div>
                  
                  <div className="space-y-3">
                    {group.focus.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700 text-sm">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* EYFS Seven Areas - Visual with Icons */}
            <div className="bg-slate-50 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-slate-800 mb-8 text-center">EYFS Seven Areas of Learning & Development</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {childcareData.services.eyfsCore.learningAreas.map((area, index) => {
                  const IconComponent = eyfsIcons[area.icon];
                  return (
                    <div key={index} className="group bg-blue-50 rounded-3xl p-8 border border-blue-100 hover:shadow-xl hover:-translate-y-1 transition-all">
                      <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                        <IconComponent className="h-8 w-8 text-blue-500" />
                      </div>
                      <h4 className="text-lg font-bold text-slate-800 mb-3">{area.title}</h4>
                      <p className="text-slate-600 text-sm leading-relaxed">{area.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Unique Additional Activities */}
        <section className="py-20 bg-pink-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                What Makes Us 
                <span className="text-pink-500"> Special</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-4xl mx-auto">
                {childcareData.services.uniqueCurriculum.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {childcareData.services.uniqueCurriculum.activities.map((activity, index) => {
                const IconComponent = activityIcons[activity.icon];
                return (
                  <div key={index} className="bg-white rounded-3xl p-8 border border-pink-100 hover:shadow-xl hover:-translate-y-1 transition-all group">
                    <div className="flex items-center justify-center w-20 h-20 bg-pink-100 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                      <IconComponent className="h-10 w-10 text-pink-500" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">
                      {activity.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed">
                      {activity.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Facilities & Environment */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                Our Unique 
                <span className="text-blue-500"> Environment</span>
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
              <div>
                <h3 className="text-2xl font-bold text-slate-800 mb-6">Georgian House Setting</h3>
                <div className="space-y-4">
                  {childcareData.services.facilities.map((facility, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-slate-700">{facility}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cozy-care/artifacts/iw4e59x3_PHOTO-2025-08-25-17-46-29-1.jpg"
                  alt="Children playing outdoors"
                  className="rounded-2xl shadow-lg"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_cozy-care/artifacts/j2ayrzfs_PHOTO-2025-08-25-17-46-29-2.jpg"
                  alt="Interactive learning"
                  className="rounded-2xl shadow-lg mt-8"
                />
              </div>
            </div>

            {/* Photo Gallery Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_cozy-care/artifacts/s2m2fhrj_PHOTO-2025-08-25-17-46-29-3.jpg"
                alt="Additional indoor play area"
                className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow h-64 w-full object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_cozy-care/artifacts/ykkk7otj_PHOTO-2025-08-25-17-46-31-2.jpg"
                alt="Reading corner with large teddy bear"
                className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow h-64 w-full object-cover"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_romantic-brahmagupta/artifacts/fsvyplu1_image.png"
                alt="Beautiful indoor learning environment with natural light"
                className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow h-64 w-full object-cover"
              />
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-blue-500 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Experience Our Unique Approach?
            </h2>
            <p className="text-xl opacity-90 mb-8 max-w-3xl mx-auto">
              Book a tour today to see our curriculum and unique activities in action. 
              Your child's educational journey starts here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => window.location.href = '/contact'}
                className="bg-white text-blue-500 hover:bg-gray-50 px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105 shadow-lg"
              >
                Book a Tour
              </button>
              <button 
                onClick={() => window.location.href = '/funding'}
                className="border-2 border-white text-white hover:bg-white hover:text-blue-500 px-8 py-4 rounded-full font-semibold transition-all"
              >
                Learn About Funding
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Services;